import { Button, Col, Flex, Form, InputNumber, Row } from "antd";
import React, { useEffect, useLayoutEffect, useState } from "react";
// import { UI_ITOModuleSuccess } from "../../../../../UI_ITO/UI_ITOModuleSuccess/UI_ITOModuleSuccess";
import { useForm } from "antd/es/form/Form";
import { nanoid } from "nanoid";
import {
  formatter,
  parser,
} from "../../../../../utils/utilities/InputSumFunctions";
/* import { ruleSummMinMax } from "../../../../../constants/rules/RulesConstantsForms"; */
import { useGetApplicationDataToBankQuery } from "../../../../../store/api/applicationBankApi/applicationBank.api";
import { useParams } from "react-router-dom";
import { Guard } from "../../../../../utils/guards/Guard";
import { APPLICATION_BANK_STATUS } from "../../../../../utils/guards/ApplicationBankStatusGuard/types";
import dayjs from "dayjs";
import {
  calculateAgentCommission,
  calculateBaseCommission,
  calculateFinalCommission,
} from "./commissionFormulas";
import {
  useChangeOfferMutation,
  useGenerateOfferDocumentsMutation,
} from "../../../../../store/api/applicationBankApi/offer.api";

const formFields = [
  {
    label: "Тариф банка",
    suffix: "₽",
    name: "base_commission",
    unitOfMeasurement: "rubles",
    minValue: "890",
  },
  {
    label: "Ставка",
    suffix: "%",
    name: "bank_rate",
    unitOfMeasurement: "percent",
    maxValue: "100",
    minValue: "0.1",
  },
  {
    label: "Завышение",
    suffix: "₽",
    name: "overstatement",
    unitOfMeasurement: "rubles",
  },
  {
    label: "Итоговая комиссия",
    suffix: "₽",
    name: "final_commission",
    unitOfMeasurement: "rubles",
  },
  {
    label: "Комиссия агента",
    suffix: "₽",
    name: "agent_commission",
    unitOfMeasurement: "rubles",
  },
];

export const ApplicationCommission = () => {
  const { applicationBankId } = useParams();
  const { data: applicationBank } = useGetApplicationDataToBankQuery(
    Number(applicationBankId)
  );
  const sumBg = Number(applicationBank?.application.application_sum_bg);

  const [changeOffer, { isLoading: isChangeStatusLoading }] =
    useChangeOfferMutation();
  const [generateDocuments, { isLoading: isGenerateDocumentsLoading }] =
    useGenerateOfferDocumentsMutation();

  const [isFormSaved, setIsFormSaved] = useState(false);

  const [form] = useForm();

  const initialValues = {
    base_commission: Number(applicationBank?.base_commission) || 890,
    bank_rate: Number(applicationBank?.bank_rate),
    overstatement: Number(applicationBank?.overstatement),
    final_commission: Number(applicationBank?.final_commission),
    agent_commission: Number(applicationBank?.agent_commission),
  };

  // Подставляем initialValues в форму, когда появляются результаты выполнения запроса
  useLayoutEffect(() => {
    if (applicationBank) {
      form.resetFields();
    }
  }, [applicationBank, form]);

  const handleSaveOffer = () => {
    changeOffer({
      applicationBankId: Number(applicationBankId),
      changeOfferDto: form.getFieldsValue(true),
    }).then(() => {
      if (!isChangeStatusLoading) {
        setIsFormSaved(true);
      }
    });
  };

  const handleGenerateDocuments = () => {
    generateDocuments(Number(applicationBankId));
  };

  /**
   * @description формулы расчета Ставки и Тарифа банка при загрузке страницы.
   * Из-за того, что они влияют на подсчет других полей, их расчеты необходимо вынести в другие функции.
   */
  const setDefaultBankRate = () => {
    if (!initialValues.bank_rate) {
      form.setFieldValue("bank_rate", 1.8);
      return 1.8;
    }
    return initialValues.bank_rate;
  };

  const setDefaultBaseComission = (defaultRate: number) => {
    if (applicationBank && applicationBank.base_commission < 890) {
      const startDate = dayjs(applicationBank.application.start_bg_at);
      const finishDate = dayjs(applicationBank.application.finish_bg_at);
      const differenceInDays = finishDate.diff(startDate, "day");

      const newBaseComission = calculateBaseCommission(
        sumBg,
        defaultRate,
        differenceInDays
      );
      form.setFieldValue("base_commission", newBaseComission);
      return newBaseComission;
    }
    return applicationBank?.base_commission || 890;
  };

  /**
   * @description Расчет формул для базовых значений некоторых полей. Формулы для полей спросить (следует гдето записать их)
   */
  useEffect(() => {
    if (applicationBank && sumBg) {
      const defaultRate = setDefaultBankRate();
      const defaultBaseComission = setDefaultBaseComission(defaultRate);

      //Логика расчета первичного значения итоговой комиссии
      if (!initialValues.final_commission) {
        form.setFieldValue(
          "final_commission",
          calculateFinalCommission(
            Number(defaultBaseComission),
            Number(applicationBank.overstatement)
          )
        );
      }
      // Логика расчета первичного значения в строке "Комиссия агента"
      if (!initialValues.agent_commission) {
        form.setFieldValue(
          "agent_commission",
          calculateAgentCommission(
            Number(defaultBaseComission),
            Number(applicationBank.overstatement)
          )
        );
      }
    }
  }, [applicationBank]);

  /**
   * @description Автоподсчет поля "Финальная комиссия"
   */
  const changeFinalCommission = () => {
    const baseComissionValue = form.getFieldValue("base_commission");
    const overstatementValue = form.getFieldValue("overstatement");

    form.setFieldValue(
      "final_commission",
      calculateFinalCommission(baseComissionValue, overstatementValue)
    );
  };

  /**
   * @description Автоподсчет поля "Комиссия агента"
   */
  const changeAgentCommission = () => {
    const overstatementValue = form.getFieldValue("overstatement");
    const baseCommissionValue = form.getFieldValue("base_commission");

    form.setFieldValue(
      "agent_commission",
      calculateAgentCommission(baseCommissionValue, overstatementValue)
    );

    changeFinalCommission();
  };

  /**
   * @description Автоподсчет поля "Тариф банка"
   */
  const changeBaseCommissionValue = () => {
    const bankRateValue = form.getFieldValue("bank_rate");
    if (applicationBank && sumBg) {
      const startDate = dayjs(applicationBank.application.start_bg_at);
      const finishDate = dayjs(applicationBank.application.finish_bg_at);
      const differenceInDays = finishDate.diff(startDate, "day");

      form.setFieldValue(
        "base_commission",
        calculateBaseCommission(sumBg, bankRateValue, differenceInDays)
      );
      changeAgentCommission();
    }
  };

  return (
    <>
      <p>Комиссия заявки</p>
      <Form
        layout="vertical"
        form={form}
        initialValues={initialValues}
        onValuesChange={(changedField) => {
          const fieldObject = Object.entries(changedField);
          if (
            fieldObject[0][0] === "overstatement" ||
            fieldObject[0][0] === "base_commission"
          ) {
            changeAgentCommission();
          }
          if (fieldObject[0][0] === "bank_rate") {
            changeBaseCommissionValue();
          }
        }}
        onFinish={handleSaveOffer}
      >
        <Row gutter={24} align="bottom">
          {formFields.map((field) => {
            const key = nanoid();
            if (field) {
              return (
                <Col
                  span={12}
                  key={key}
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={12}
                >
                  <Form.Item
                    label={field.label}
                    name={field.name}
                    /* rules={
                      field.name === "base_commission"
                        ? [ruleBaseCommission]
                        : undefined
                    } */
                  >
                    <InputNumber
                      disabled={
                        applicationBank?.modified_bank_status.type !==
                          APPLICATION_BANK_STATUS.OFFER &&
                        applicationBank?.modified_bank_status.type !==
                          APPLICATION_BANK_STATUS.AGREED
                      }
                      min={field.minValue}
                      max={field.maxValue}
                      suffix={field.suffix}
                      controls={false}
                      style={{ width: "100%" }}
                      {...(field.unitOfMeasurement === "rubles" && {
                        formatter,
                        parser,
                      })}
                    />
                  </Form.Item>
                </Col>
              );
            }
          })}
        </Row>

        <Flex justify="flex-end" align="center" gap={8}>
          {/* <UI_ITOModuleSuccess
            title="Данные были изменены, необходимо сохранение"
            isSuccess={isFormSaved}
          /> */}
          {!!applicationBank && (
            <Guard.ApplicationBankStatus
              status={applicationBank.modified_bank_status.type}
              allowedStatuses={[
                APPLICATION_BANK_STATUS.DIRECTED,
                APPLICATION_BANK_STATUS.REVISION,
                APPLICATION_BANK_STATUS.OFFER,
                APPLICATION_BANK_STATUS.AGREED,
              ]}
            >
              <Button
                type="default"
                onClick={handleGenerateDocuments}
                disabled={!isFormSaved}
                loading={isGenerateDocumentsLoading}
              >
                Сформировать документы
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                loading={isChangeStatusLoading}
              >
                Сохранить
              </Button>
            </Guard.ApplicationBankStatus>
          )}
        </Flex>
      </Form>
    </>
  );
};
